import { useGlobalModal } from '@/hooks/useGlobalModal';
import DeviceModalTitle from '@components/DeviceModalTitle';
import GlobalModal from '@components/GlobalModal';
import ModalTabs, { Panel } from '@components/ModalTabs';
import { Access } from '@features/authorization';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useEffect, useState, type FC } from 'react';
import { TabSuspenseWrapper } from './common';
import { AllAccessTab } from './allAccessTab';
import { CollectionsAccessTab } from './collectionsAccessTab';
import { DeviceAccessTab } from './deviceAccessTab';
import { DeviceAccessModalTab } from './types';

export const DeviceAccessModal: FC = () => {
  const { globalModalProps, state, onClose, changeTabMiddleware, setIsDirty } = useGlobalModal(
    'deviceAccessModal',
    {
      closeConfirmation: true,
    },
  );

  const { panels, value, setValue } = usePanels({
    deviceId: 'deviceId' in state ? state.deviceId : null,
    initialValue: 'selectedTab' in state ? state.selectedTab ?? null : null,
    onClose,
    setIsDirty,
  });

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'xl' }}
      authorization={{
        access: Access.DeviceAccessModal,
        deviceId: state.open ? state.deviceId : undefined,
        onClose,
      }}
    >
      {state.open && (
        <>
          <DeviceModalTitle deviceId={state.deviceId} onClose={onClose} />
          <ModalTabs
            deviceId={state.deviceId}
            value={value}
            setValue={setValue}
            changeTabMiddleware={changeTabMiddleware}
            panels={panels}
          />
        </>
      )}
    </GlobalModal>
  );
};

const usePanels = ({
  deviceId,
  initialValue,
  onClose,
  setIsDirty,
}: {
  deviceId: string | null;
  initialValue: DeviceAccessModalTab | null;
  onClose: () => void;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}): {
  panels: Array<Panel<DeviceAccessModalTab>>;
  value: DeviceAccessModalTab;
  setValue: React.Dispatch<React.SetStateAction<DeviceAccessModalTab>>;
} => {
  const { _ } = useLingui();
  const [selectedTab, setSelectedTab] = useState(DeviceAccessModalTab.All);

  useEffect(() => {
    // Mirror de AMARU-562 (userConfigurationModal): resetear el tab activo cada vez que el
    // modal se abre para un dispositivo — incluye cerrar y reabrir para el mismo u otro.
    setSelectedTab(initialValue ?? DeviceAccessModalTab.All);
  }, [deviceId, initialValue, setSelectedTab]);

  if (!deviceId)
    return {
      panels: [],
      value: selectedTab,
      setValue: setSelectedTab,
    };

  const props = {
    deviceId,
    onClose,
    setIsDirty,
  };

  return {
    panels: [
      {
        key: DeviceAccessModalTab.All,
        label: _(msg`Todos los accesos`),
        component: (
          <TabSuspenseWrapper onClose={onClose}>
            <AllAccessTab deviceId={deviceId} onClose={onClose} />
          </TabSuspenseWrapper>
        ),
        access: Access.DeviceAccessModal,
      },
      {
        key: DeviceAccessModalTab.Collections,
        label: _(msg`Acceso por colección`),
        component: (
          <TabSuspenseWrapper onClose={onClose}>
            <CollectionsAccessTab {...props} />
          </TabSuspenseWrapper>
        ),
        access: Access.DeviceAccessModal,
      },
      {
        key: DeviceAccessModalTab.Access,
        label: _(msg`Acceso directo`),
        component: (
          <TabSuspenseWrapper onClose={onClose}>
            <DeviceAccessTab {...props} />
          </TabSuspenseWrapper>
        ),
        access: Access.DeviceAccessModal,
      },
    ],
    value: selectedTab,
    setValue: setSelectedTab,
  };
};
